// src/components/SPanel/Home/HomeGrid.jsx
//
// Grila de widget-uri a paginii principale, cu modul „Editează” ca pe iPhone:
// widget-urile se clatină, au „−” (scoate) și mărimea (1×1 / 2×1 / 2×2), se
// mută trăgându-le (cu mouse-ul direct, cu degetul după o apăsare scurtă, ca
// pagina să se poată derula în continuare). Celelalte își fac loc animat.
//
// Celulele sunt pătrate: lățimea unei coloane se măsoară și devine înălțimea
// rândului (înmulțită cu mărimea textului din Setări, ca textul să încapă).

import React, {
   useCallback,
   useEffect,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
} from "react";

import { prefersReducedMotion } from "../../../utils/appearanceSettings";
import { triggerHapticFeedback } from "../../../utils/hapticFeedback";
import { setHomeEditActions, setHomeEditing, useHomeEditing } from "./homeEditStore";
import {
   SIZES,
   cycleSize,
   defaultLayout,
   gridColumns,
   moveWidget,
   removeWidget,
   rowHeight,
   widgetInfo,
} from "./homeLayout";

const LONG_PRESS_MS = 220;
const MOUSE_SLOP_PX = 4;
const TOUCH_SLOP_PX = 8;
const REORDER_COOLDOWN_MS = 140;
const EDGE_SCROLL_PX = 80;
const FLIP_EASING = "cubic-bezier(0.2, 0.8, 0.2, 1)";

function sizeLabel(size) {
   return size.replace("x", "×");
}

// `savedLayout` null = aranjamentul implicit al ecranului (telefon / ecran lat)
export default function HomeGrid({ savedLayout, onChange, renderWidget, onAdd, onReset, isAvailable }) {
   const gridRef = useRef(null);
   const itemRefs = useRef(new Map());
   const prevRectsRef = useRef(null);
   const dragRef = useRef(null);

   const [metrics, setMetrics] = useState({ cols: 2, cell: 170, gap: 8 });
   // widget-urile indisponibile acum (de ex. balanța oprită) nu se desenează
   const layout = useMemo(() => {
      const full = savedLayout || defaultLayout(metrics.cols);
      return isAvailable ? full.filter((item) => isAvailable(item.id)) : full;
   }, [savedLayout, metrics.cols, isAvailable]);
   const layoutRef = useRef(layout);
   layoutRef.current = layout;
   // „Editează”, „Adaugă”, „Resetează” și „Gata” sunt în rândul de sus (homeEditStore)
   const editing = useHomeEditing();
   useEffect(() => () => setHomeEditing(false), []);
   const [draggingId, setDraggingId] = useState(null);

   /* ——— măsurare: coloane și latura unei celule ——— */
   useLayoutEffect(() => {
      const node = gridRef.current;
      if (!node || typeof ResizeObserver === "undefined") return undefined;
      const observer = new ResizeObserver(([entry]) => {
         const width = entry.contentRect.width;
         const cols = gridColumns(width);
         const gap = width < 560 ? 8 : 10;
         const cell = Math.floor((width - gap * (cols - 1)) / cols);
         setMetrics((prev) =>
            prev.cols === cols && prev.cell === cell && prev.gap === gap
               ? prev
               : { cols, cell, gap },
         );
      });
      observer.observe(node);
      return () => observer.disconnect();
   }, []);

   /* ——— animația „FLIP”: widget-urile alunecă în locurile noi ——— */
   const captureRects = useCallback(() => {
      const rects = new Map();
      itemRefs.current.forEach((el, id) => rects.set(id, el.getBoundingClientRect()));
      prevRectsRef.current = rects;
   }, []);

   const commit = useCallback(
      (next) => {
         if (next === layoutRef.current) return;
         captureRects();
         onChange(next);
      },
      [captureRects, onChange],
   );

   // poziția widget-ului tras, față de locul lui din grilă
   const placeDragged = useCallback(() => {
      const drag = dragRef.current;
      if (!drag?.active) return;
      const el = itemRefs.current.get(drag.id);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const baseLeft = rect.left - drag.tx;
      const baseTop = rect.top - drag.ty;
      drag.tx = drag.x - drag.grabX - baseLeft;
      drag.ty = drag.y - drag.grabY - baseTop;
      el.style.transform = `translate(${drag.tx}px, ${drag.ty}px)`;
   }, []);

   useLayoutEffect(() => {
      const prev = prevRectsRef.current;
      prevRectsRef.current = null;
      placeDragged();
      if (!prev || prefersReducedMotion()) return;
      const draggedId = dragRef.current?.active ? dragRef.current.id : null;
      itemRefs.current.forEach((el, id) => {
         if (id === draggedId) return;
         const before = prev.get(id);
         if (!before) {
            el.animate(
               [
                  { opacity: 0, transform: "scale(0.85)" },
                  { opacity: 1, transform: "none" },
               ],
               { duration: 320, easing: FLIP_EASING },
            );
            return;
         }
         const after = el.getBoundingClientRect();
         const dx = before.left - after.left;
         const dy = before.top - after.top;
         if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
         el.animate(
            [
               { transform: `translate(${dx}px, ${dy}px)` },
               { transform: "translate(0, 0)" },
            ],
            { duration: 300, easing: FLIP_EASING },
         );
      });
   }, [layout, metrics, placeDragged]);

   /* ——— tragerea ——— */
   const activateDrag = useCallback(() => {
      const drag = dragRef.current;
      if (!drag || drag.active) return;
      drag.active = true;
      setDraggingId(drag.id);
      itemRefs.current.get(drag.id)?.setPointerCapture?.(drag.pointerId);
      // vibrația scurtă de la ridicarea widget-ului (Android; pe iOS din 18, prin comutator)
      triggerHapticFeedback(8);
   }, []);

   const endDrag = useCallback(() => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag) return;
      window.clearTimeout(drag.timer);
      if (!drag.active) return;
      const el = itemRefs.current.get(drag.id);
      if (el) {
         const from = `translate(${drag.tx}px, ${drag.ty}px)`;
         el.style.transform = "";
         if (!prefersReducedMotion()) {
            el.animate([{ transform: from }, { transform: "translate(0, 0)" }], {
               duration: 260,
               easing: FLIP_EASING,
            });
         }
      }
      setDraggingId(null);
   }, []);

   const onItemPointerDown = (event, id) => {
      if (!editing || dragRef.current) return;
      if (event.button !== undefined && event.button !== 0) return;
      if (event.target.closest?.(".hw__control")) return;
      const el = itemRefs.current.get(id);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const isMouse = event.pointerType === "mouse";
      dragRef.current = {
         id,
         pointerId: event.pointerId,
         isMouse,
         startX: event.clientX,
         startY: event.clientY,
         x: event.clientX,
         y: event.clientY,
         grabX: event.clientX - rect.left,
         grabY: event.clientY - rect.top,
         tx: 0,
         ty: 0,
         active: false,
         lastMoveAt: 0,
         timer: isMouse ? 0 : window.setTimeout(activateDrag, LONG_PRESS_MS),
      };
   };

   const onItemPointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      drag.x = event.clientX;
      drag.y = event.clientY;
      const moved = Math.hypot(drag.x - drag.startX, drag.y - drag.startY);

      if (!drag.active) {
         if (drag.isMouse && moved > MOUSE_SLOP_PX) activateDrag();
         // degetul a plecat înainte de apăsarea lungă: elevul derulează pagina
         else if (!drag.isMouse && moved > TOUCH_SLOP_PX) {
            window.clearTimeout(drag.timer);
            dragRef.current = null;
         }
         return;
      }

      placeDragged();

      // aproape de marginea ecranului, pagina se derulează singură
      if (drag.y < EDGE_SCROLL_PX) window.scrollBy(0, -12);
      else if (drag.y > window.innerHeight - EDGE_SCROLL_PX) window.scrollBy(0, 12);

      // locul de sub deget: widget-ul tras ia poziția celui de acolo
      const now = performance.now();
      if (now - drag.lastMoveAt < REORDER_COOLDOWN_MS) return;
      // (widget-ul tras e chiar sub deget: îl sărim)
      const under = (document.elementsFromPoint?.(drag.x, drag.y) || [])
         .map((node) => node.closest?.("[data-widget-id]"))
         .find((node) => node && node.dataset.widgetId !== drag.id);
      const targetId = under?.dataset.widgetId;
      if (!targetId || targetId === drag.id) return;
      const toIndex = layoutRef.current.findIndex((item) => item.id === targetId);
      drag.lastMoveAt = now;
      commit(moveWidget(layoutRef.current, drag.id, toIndex));
   };

   // cât timp un widget e tras cu degetul, pagina nu se derulează
   useEffect(() => {
      if (!editing) return undefined;
      const onTouchMove = (event) => {
         if (dragRef.current?.active) event.preventDefault();
      };
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      return () => window.removeEventListener("touchmove", onTouchMove);
   }, [editing]);

   // „Gata” din rândul de sus: widget-ul tras (dacă e) se așază la loc
   useEffect(() => {
      if (!editing) endDrag();
   }, [editing, endDrag]);

   // „Adaugă” / „Resetează” din rândul de sus
   useEffect(() =>
      setHomeEditActions({
         add: () => onAdd(layoutRef.current),
         reset: () => {
            captureRects();
            onReset();
         },
      }),
   );

   return (
      <div className={`hgrid-wrap${editing ? " is-editing" : ""}`}>
         <div
            ref={gridRef}
            className="hgrid"
            style={{
               "--cols": metrics.cols,
               "--cell": `${rowHeight(metrics.cell, metrics.cols)}px`,
               "--gap": `${metrics.gap}px`,
            }}
         >
            {layout.map((item, index) => {
               const size = SIZES[item.size] || SIZES["1x1"];
               const info = widgetInfo(item.id);
               const canResize = (info?.sizes.length || 0) > 1;
               return (
                  <div
                     key={item.id}
                     ref={(el) => {
                        if (el) itemRefs.current.set(item.id, el);
                        else itemRefs.current.delete(item.id);
                     }}
                     data-widget-id={item.id}
                     className={
                        "hw" +
                        ` hw--${item.size}` +
                        (draggingId === item.id ? " is-dragging" : "")
                     }
                     style={{
                        gridColumn: `span ${Math.min(size.w, metrics.cols)}`,
                        gridRow: `span ${size.h}`,
                        "--i": index,
                     }}
                     onPointerDown={(event) => onItemPointerDown(event, item.id)}
                     onPointerMove={onItemPointerMove}
                     onPointerUp={endDrag}
                     onPointerCancel={endDrag}
                     onContextMenu={editing ? (event) => event.preventDefault() : undefined}
                  >
                     <div className="hw__jiggle">
                        {renderWidget(item)}

                        {editing && (
                           <>
                              <button
                                 type="button"
                                 className="hw__control hw__remove"
                                 aria-label={`Scoate „${info?.title || item.id}”`}
                                 onClick={() => commit(removeWidget(layoutRef.current, item.id))}
                              >
                                 <span aria-hidden="true" />
                              </button>
                              {canResize && (
                                 <button
                                    type="button"
                                    className="hw__control hw__resize"
                                    aria-label={`Mărimea: ${sizeLabel(item.size)}. Schimbă`}
                                    onClick={() => commit(cycleSize(layoutRef.current, item.id))}
                                 >
                                    {sizeLabel(item.size)}
                                 </button>
                              )}
                           </>
                        )}
                     </div>
                  </div>
               );
            })}
         </div>

         {editing && layout.length === 0 && (
            <p className="hgrid-empty">
               Nu ai niciun widget. Apasă „Adaugă” ca să pui unul.
            </p>
         )}
      </div>
   );
}
